import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { registerSW } from "virtual:pwa-register";
import ErrorBoundary from "./components/ErrorBoundary";
import { supabase } from "./integrations/supabase/client";
import { getCollectedLogs, getBrowserInfo } from "./utils/logCollector";
import { installChunkErrorHandlers, markBootHealthy } from "./utils/chunkErrorHandler";
import { purgeRogueServiceWorkerCachesOnce } from "./utils/swCachePurge";

// ---------------------------------------------------------------------------
// `Object.hasOwn` polyfill for Safari < 15.4 / iOS < 15.4
//
// `react-markdown@9` (also reached transitively via `@uiw/react-markdown-preview`
// and `@uiw/react-md-editor`) calls `Object.hasOwn(...)` inside its option
// validation, which runs on every render. The method is unavailable on
// iOS/iPadOS 14 and crashes the affected route with
// `TypeError: Object.hasOwn is not a function`. The maintainers refuse to
// backport (see remarkjs/react-markdown#804) and esbuild's `build.target`
// only lowers syntax, not runtime globals.
//
// Installed before `createRoot(...).render(...)` below so the global is in
// place before any React render kicks off.
//
// `Object.hasOwn` is ES2022; tsconfig targets ES2020 so it isn't in the lib
// types. Cast to a typed shim instead of widening the project's lib.
// ---------------------------------------------------------------------------
const objectWithHasOwn = Object as unknown as {
  hasOwn?: (target: object, propertyKey: PropertyKey) => boolean;
};
if (typeof objectWithHasOwn.hasOwn !== "function") {
  objectWithHasOwn.hasOwn = (target, propertyKey) =>
    Object.prototype.hasOwnProperty.call(target, propertyKey);
}

// Recover from stale-deploy chunk load errors (missing JS chunks returning
// index.html) by triggering a one-time page reload. Must run before any
// dynamic import to catch the earliest failure.
installChunkErrorHandlers();

// ---------------------------------------------------------------------------
// DOM safety net
//
// Browser extensions, translators, and third-party libraries sometimes remove
// or reparent DOM nodes that React's fiber tree still references. When React
// later calls removeChild / insertBefore on a stale reference the app crashes
// with "NotFoundError: The node to be removed is not a child of this node".
//
// These patches make the native methods no-ops when the node isn't actually a
// child, turning a fatal crash into a silent skip. This is a last-resort
// safety net — the real fixes are in application code.
//
// Every intercepted call is batched and reported to the silent_crash_reports
// table so the team has visibility into how often extensions/translators
// interfere.
// ---------------------------------------------------------------------------

const DOM_SAFETY_COOLDOWN_MS = 30_000;
const DOM_SAFETY_DEBOUNCE_MS = 2_000;
const DOM_SAFETY_COOLDOWN_KEY = "curio_last_dom_safety_report";

let domSafetyBuffer: string[] = [];
let domSafetyTimer: ReturnType<typeof setTimeout> | null = null;

function flushDomSafetyReport(): void {
  if (domSafetyBuffer.length === 0) return;

  try {
    const lastSent = localStorage.getItem(DOM_SAFETY_COOLDOWN_KEY);
    if (lastSent && Date.now() - Number(lastSent) < DOM_SAFETY_COOLDOWN_MS) {
      domSafetyBuffer = [];
      return;
    }
  } catch {
    // localStorage unavailable — allow the report
  }

  const entries = domSafetyBuffer.splice(0);

  const row = {
    error_name: "DomSafetyNet",
    error_message: `${entries.length} intercepted DOM operation(s)`,
    error_stack: entries.join("\n"),
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    console_logs: getCollectedLogs(),
    browser_info: getBrowserInfo(),
  };

  try {
    localStorage.setItem(DOM_SAFETY_COOLDOWN_KEY, String(Date.now()));
  } catch {
    // ignore
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (supabase.from as any)("silent_crash_reports")
    .insert(row)
    .then(({ error }: { error: { message: string } | null }) => {
      if (error) {
        console.error("[DOM safety net] Failed to report:", error.message);
      }
    });
}

function queueDomSafetyEntry(operation: string, detail: string): void {
  domSafetyBuffer.push(`[${new Date().toISOString()}] ${operation}: ${detail}`);
  if (domSafetyTimer) clearTimeout(domSafetyTimer);
  domSafetyTimer = setTimeout(flushDomSafetyReport, DOM_SAFETY_DEBOUNCE_MS);
}

function describeNode(node: Node): string {
  if (node instanceof HTMLElement) return `<${node.tagName.toLowerCase()}.${node.className}>`;
  if (node.nodeType === Node.TEXT_NODE) return `#text("${(node.textContent || "").slice(0, 40)}")`;
  return `[${node.nodeName}]`;
}

if (typeof Node !== "undefined") {
  const originalRemoveChild = Node.prototype.removeChild;

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      const detail = `parent=${describeNode(this)} child=${describeNode(child)}`;
      console.warn("[DOM safety net] removeChild skipped:", detail);
      queueDomSafetyEntry("removeChild", detail);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      const detail = `parent=${describeNode(this)} ref=${describeNode(referenceNode)}`;
      console.warn("[DOM safety net] insertBefore skipped:", detail);
      queueDomSafetyEntry("insertBefore", detail);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

// Register service worker manually to bundle it with main chunk.
//
// We also wire up the lifecycle callbacks so the app can self-heal from
// stale-bundle situations (typical iPad-in-classroom pattern: tab stays open
// for hours/days and never picks up new deploys without a manual refresh).
//
// Three layers of freshness:
//   1. Periodic background `registration.update()` every hour.
//   2. Re-check whenever the tab regains visibility.
//   3. When a new SW is ready (`onNeedRefresh`), trigger an automatic reload —
//      immediately on /auth (no in-flight state, biggest stability win),
//      deferred until next visibility change elsewhere (so we don't interrupt
//      a student mid tutor-chat).
const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000;
let needsRefresh = false;

// One-time purge of the pre-fix Workbox worker that cached Supabase Auth
// responses (see swCachePurge.ts). Fire-and-forget: must never block boot.
// Runs before registerSW so the fresh worker installs onto clean caches.
void purgeRogueServiceWorkerCachesOnce();

const updateSW = registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;

    setInterval(() => {
      registration.update().catch(() => {
        // Network / SW errors here are non-fatal; we'll re-check next tick.
      });
    }, SW_UPDATE_INTERVAL_MS);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        registration.update().catch(() => {
          // see above
        });
      }
    });
  },
  onNeedRefresh() {
    needsRefresh = true;

    if (window.location.pathname === "/auth") {
      void updateSW(true);
      return;
    }

    const reloadOnNextFocus = () => {
      if (document.visibilityState === "visible" && needsRefresh) {
        document.removeEventListener("visibilitychange", reloadOnNextFocus);
        void updateSW(true);
      }
    };
    document.addEventListener("visibilitychange", reloadOnNextFocus);
  },
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

// Once the tab has been alive long enough that we are confident the build is
// healthy, clear the per-tab chunk-recovery counter so a future stale-deploy
// situation gets the full recovery budget instead of starting at "give up".
markBootHealthy();
